import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../Layout/Header';
import Footer from '../../Layout/Footer';
import FAQ from './FAQ';
import SeoTagsFooter from './SeoTagsFooter';
import Seo from '../../Layout/Seo';

function ServiceDetail() {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const titleParam = searchParams.get('title') || searchParams.get('service') || 'Fintech & Software Solutions';
  const categoryParam = searchParams.get('category') || 'Mokshiq Platform Services';

  // Comprehensive custom database for dropdown items
  const getCustomContent = (title, category) => {
    const titleLower = title.toLowerCase();

    // 1. RECHARGE & BILL PAYMENT ITEMS
    if (titleLower.includes('recharge') || titleLower.includes('ott') || titleLower.includes('fastag') || titleLower.includes('emi') || titleLower.includes('lic') || titleLower.includes('courier') || titleLower.includes('sms') || titleLower.includes('whatsapp')) {
      return {
        badge: "RECHARGE & MESSAGING ENGINE",
        heroSubtitle: `Automate ${title} with high commission margins, 99.9% uptime, and instant LAPU fallback servers powered by Rakle Service Private Limited.`,
        icon: "fa-mobile-screen-button",
        specs: [
          { label: "API Speed", value: "< 200ms Response Time" },
          { label: "Success Rate", value: "99.9% Automated SLA" },
          { label: "Lapu Fallback", value: "Multi-Route Redundancy" },
          { label: "Refunds", value: "Instant Balance Reversal" }
        ],
        codeSampleRequest: `{
  "api_key": "YOUR_MOKSHIQ_API_KEY",
  "service_type": "${title.toUpperCase().replace(/\s+/g, '_')}",
  "account_no": "9812739182",
  "amount": "299",
  "operator_code": "JIO"
}`,
        codeSampleResponse: `{
  "status": "SUCCESS",
  "txid": "MKQ${Math.floor(100000000 + Math.random() * 90000000)}",
  "operator_ref": "REF981293812",
  "amount": 299,
  "margin_earned": 8.97,
  "message": "${title} Transaction Executed Successfully"
}`,
        features: [
          { icon: "fa-bolt", title: "Instant Execution", desc: "Process transactions within milliseconds through direct telecom and biller integrations." },
          { icon: "fa-sliders", title: "Automated Fallback", desc: "Multi-server LAPU switching ensures zero transaction drop even during peak traffic." },
          { icon: "fa-wallet", title: "Highest Margin Slab", desc: "Earn up to 4.5% flat margin on mobile, DTH, and utility bill payments." },
          { icon: "fa-bell", title: "Real-Time Webhooks", desc: "Receive immediate transaction status callbacks to your server endpoint." }
        ]
      };
    }

    // 2. BANKING & FINTECH ITEMS (AEPS, DMT, mATM, Aadhaar Pay, Payout)
    if (titleLower.includes('aeps') || titleLower.includes('dmt') || titleLower.includes('matm') || titleLower.includes('payout') || titleLower.includes('remittance') || titleLower.includes('banking') || titleLower.includes('neo-banking')) {
      return {
        badge: "FINTECH & BANKING INFRASTRUCTURE",
        heroSubtitle: `Enterprise-grade ${title} solution with instant IMPS/NEFT settlement, biometric authentication, and RBI-compliant security.`,
        icon: "fa-building-columns",
        specs: [
          { label: "Network", value: "NPCI & Bank Direct Node" },
          { label: "Settlement", value: "24x7 Instant Wallet Cashout" },
          { label: "Encryption", value: "256-bit Bank Grade SSL" },
          { label: "Biometric", value: "Mantra, Morpho, Startek RD" }
        ],
        codeSampleRequest: `{
  "api_key": "YOUR_MOKSHIQ_API_KEY",
  "service": "${title.toUpperCase().replace(/\s+/g, '_')}",
  "aadhaar_number": "XXXX-XXXX-1234",
  "bank_iin": "607152",
  "amount": "2000"
}`,
        codeSampleResponse: `{
  "status": "SUCCESS",
  "bank_rrn": "428192830192",
  "balance_remaining": "14500.00",
  "commission": "10.00",
  "message": "${title} Approved by NPCI Node"
}`,
        features: [
          { icon: "fa-shield-halved", title: "RBI & NPCI Compliant", desc: "Operates with 2FA Aadhaar Pay authentication and encrypted biometric RD services." },
          { icon: "fa-arrow-right-arrow-left", title: "24x7 Money Transfer", desc: "Instant IMPS settlement directly into any Bank Account across India." },
          { icon: "fa-money-bill-wave", title: "Maximum Retailer Margin", desc: "Earn up to ₹12 flat commission per cash withdrawal and money transfer transaction." },
          { icon: "fa-circle-check", title: "Zero Balance Holding", desc: "Automated wallet reconciliation with instant refund callbacks on failed requests." }
        ]
      };
    }

    // 3. VERIFICATION & KYC ITEMS
    if (titleLower.includes('verify') || titleLower.includes('pan') || titleLower.includes('aadhaar') || titleLower.includes('gstin') || titleLower.includes('vehicle') || titleLower.includes('crm') || titleLower.includes('pos')) {
      return {
        badge: "IDENTITY & COMPLIANCE ENGINE",
        heroSubtitle: `Verify customer identity, business documents, and bank details in real-time with Mokshiq ${title}.`,
        icon: "fa-id-card",
        specs: [
          { label: "Lookup Speed", value: "< 400ms Response Time" },
          { label: "Database", value: "Direct NSDL / ITD / UIDAI" },
          { label: "Compliance", value: "100% Legal & IT Act 2000" },
          { label: "Sandbox", value: "Free Unlimited Testing" }
        ],
        codeSampleRequest: `{
  "api_key": "YOUR_MOKSHIQ_API_KEY",
  "document_type": "${title.toUpperCase().replace(/\s+/g, '_')}",
  "input_number": "ABCDE1234F",
  "consent": "Y"
}`,
        codeSampleResponse: `{
  "status": "VALID",
  "registered_name": "RAKLE SERVICE PRIVATE LIMITED",
  "verification_timestamp": "${new Date().toISOString()}",
  "match_score": "100%"
}`,
        features: [
          { icon: "fa-shield-cat", title: "Fraud Prevention", desc: "Detect identity theft, dummy registrations, and invalid bank accounts instantly." },
          { icon: "fa-paperclip", title: "Paperless Onboarding", desc: "Eliminate manual physical document processing with instant digital API queries." },
          { icon: "fa-laptop-code", title: "Developer Friendly", desc: "Clean JSON SDKs available for PHP, Node.js, Python, Java, and Android Native." },
          { icon: "fa-lock", title: "Bank-Grade Encryption", desc: "End-to-end payload encryption following strict Indian data privacy norms." }
        ]
      };
    }

    // 4. DEVELOPMENT, HOSTING & MARKETING SERVICES
    return {
      badge: "ENTERPRISE SOFTWARE & SERVICES",
      heroSubtitle: `Custom development, high-performance hosting, and scalable solutions for ${title} by Mokshiq (Rakle Service Private Limited).`,
      icon: "fa-laptop-code",
      specs: [
        { label: "Platform", value: "Custom React, Node, Android" },
        { label: "Architecture", value: "Microservices & Cloud VPS" },
        { label: "Uptime SLA", value: "99.9% High Availability" },
        { label: "Support", value: "24x7 Dedicated Engineers" }
      ],
      codeSampleRequest: `{
  "project_type": "${title}",
  "client": "Rakle Partner Enterprise",
  "architecture": "Scalable Cloud Stack",
  "support_tier": "24x7 Dedicated SLA"
}`,
      codeSampleResponse: `{
  "deployment": "SUCCESSFUL",
  "status": "LIVE_PRODUCTION",
  "security_grade": "A+",
  "active_users_handled": "1,000,000+"
}`,
      features: [
        { icon: "fa-code", title: "Custom Code Base", desc: "Clean, modular code built on cutting-edge frameworks for maximum scalability." },
        { icon: "fa-server", title: "High-Performance Servers", desc: "Powered by SSD NVMe storage, cPanel/Plesk dashboards, and DDOS protection." },
        { icon: "fa-chart-pie", title: "Result-Driven ROI", desc: "Tailored to convert traffic into leads and streamline complex business operations." },
        { icon: "fa-headset", title: "Turnkey Maintenance", desc: "Includes regular security patches, cloud backups, and feature enhancements." }
      ]
    };
  };

  const customData = getCustomContent(titleParam, categoryParam);

  // Trim the hero subtitle to a clean ~155-char meta description — Google
  // truncates longer snippets anyway, so keep it tight and on-topic.
  const metaDescription =
    customData.heroSubtitle.length > 155
      ? `${customData.heroSubtitle.slice(0, 152)}...`
      : customData.heroSubtitle;

  const dynamicFaqs = [
    {
      question: `What is Mokshiq's ${titleParam}?`,
      answer: `Mokshiq's ${titleParam} is a enterprise-grade solution provided by Rakle Service Private Limited. It is engineered for high performance, bank-grade security, and seamless deployment.`
    },
    {
      question: `How fast can we integrate ${titleParam} into our business?`,
      answer: `You can get live within 24 hours. Our developer sandbox, ready-to-use API documentation, and dedicated onboarding team ensure quick deployment.`
    },
    {
      question: `What support and maintenance is provided with ${titleParam}?`,
      answer: `We offer 24x7 technical support, dedicated server monitoring, instant failover management, and direct developer help via hotline +91 7311104571 / sales@mokshiq.in.`
    }
  ];

  return (
    <div className="page_wrapper">
      <Seo
        title={`${titleParam} | Mokshiq`}
        description={metaDescription}
        keywords={`${titleParam}, ${categoryParam}, Mokshiq, Rakle Service Private Limited`}
        path={`/service_details${location.search}`}
        // Query-string pages are real, unique content per item, but they
        // all share one physical URL pattern (?title=...), which search
        // engines treat as low-priority for indexing compared to clean
        // path-based URLs. Keep indexable for now; if/when these move to
        // /service_details/:slug, drop this prop entirely.
        noindex={false}
      />
      <Header />
      <main className="page_content">

        {/* Hero Banner Section */}
        <section className="page_header text-center bg-dark section_decoration overflow-hidden py-5" style={{ backgroundImage: "url('assets/images/backgrounds/page_header_bg_1.webp')" }}>
          <div className="container">
            <span className="badge bg-warning text-dark px-3 py-2 text-uppercase mb-3 font-weight-bold" style={{ letterSpacing: '1px' }}>
              <i className={`fa-solid ${customData.icon} me-2`}></i>{customData.badge}
            </span>
            <h1 className="page_title text-white mb-3">{titleParam}</h1>
            <p className="text-white-50 mx-auto" style={{ maxWidth: '780px' }}>
              {customData.heroSubtitle}
            </p>
            <ul className="breadcrumb_nav unordered_list justify-content-center mt-3">
              <li><a href="/">Home</a></li>
              <li><a href="/services">{categoryParam}</a></li>
              <li className="active" aria-current="page">{titleParam}</li>
            </ul>
          </div>
        </section>

        {/* Dynamic Specifications Bar */}
        <section className="bg-light py-4 border-bottom">
          <div className="container">
            <div className="row g-3 text-center">
              {customData.specs.map((spec, idx) => (
                <div key={idx} className="col-6 col-md-3">
                  <div className="p-3 bg-white rounded-3 shadow-sm border">
                    <span className="text-muted small d-block font-weight-bold text-uppercase">{spec.label}</span>
                    <strong className="text-dark h6 mb-0 d-block mt-1">{spec.value}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Overview Section */}
        <section className="service_features_section section_space bg-white">
          <div className="container">
            <div className="heading_block text-center mb-5">
              <span className="text-primary font-weight-bold text-uppercase small">Why Choose Mokshiq</span>
              <h2 className="heading_text text-dark mt-1">High-Performance {titleParam} Features</h2>
              <p className="heading_description text-muted">Powered by <strong>Rakle Service Private Limited</strong> infrastructure built for reliability and scale.</p>
            </div>

            <div className="row g-4 mb-5">
              {customData.features.map((feat, idx) => (
                <div key={idx} className="col-md-6 col-lg-3">
                  <div className="card h-100 p-4 border-0 shadow-sm bg-white rounded-3 text-center">
                    <div className="icon_box bg-primary text-white mx-auto mb-3">
                      <i className={`fa-solid ${feat.icon}`}></i>
                    </div>
                    <h3 className="h5 font-weight-bold text-dark mb-2">{feat.title}</h3>
                    <p className="text-muted small mb-0">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Live API / Code Sample Console (Bootstrap Grid) */}
            <div className="api_code_console bg-dark text-light p-4 p-lg-5 rounded-4 shadow-lg mb-5">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary">
                <div>
                  <span className="badge bg-success text-dark fw-bold mb-1">LIVE API &amp; SERVICE PAYLOAD</span>
                  <h3 className="h4 text-warning mb-0">{titleParam} Spec model</h3>
                </div>
                <div className="mt-2 mt-md-0">
                  <a href="mailto:sales@mokshiq.in?subject=Sandbox%20Key%20Request" className="btn btn-warning btn-sm me-2 fw-bold">
                    <i className="fa-solid fa-key me-1"></i> Get Sandbox Credentials
                  </a>
                  <a href="tel:+917311104571" className="btn btn-outline-light btn-sm fw-bold">
                    <i className="fa-solid fa-phone me-1"></i> Call Support (+91 7311104571)
                  </a>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-lg-6">
                  <h5 className="text-light small text-uppercase mb-2 font-weight-bold">
                    <i className="fa-solid fa-arrow-right-to-bracket text-primary me-2"></i>Sample Request Payload
                  </h5>
                  <pre className="bg-black text-info p-3 rounded border border-secondary font-monospace mb-0" style={{ fontSize: '0.85rem' }}>
                    <code>{customData.codeSampleRequest}</code>
                  </pre>
                </div>
                <div className="col-lg-6">
                  <h5 className="text-light small text-uppercase mb-2 font-weight-bold">
                    <i className="fa-solid fa-arrow-right-from-bracket text-success me-2"></i>Sample Response Payload (200 OK)
                  </h5>
                  <pre className="bg-black text-success p-3 rounded border border-secondary font-monospace mb-0" style={{ fontSize: '0.85rem' }}>
                    <code>{customData.codeSampleResponse}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Responsive Interactive Lead / Demo Request Form */}
            <div className="p-4 p-lg-5 bg-light rounded-4 shadow-sm border">
              <div className="row align-items-center g-4">
                <div className="col-lg-6">
                  <span className="badge bg-warning text-dark font-weight-bold mb-2">TALK TO OUR EXPERTS</span>
                  <h3 className="h2 text-dark font-weight-bold mb-3">Request Sandbox &amp; Pricing for {titleParam}</h3>
                  <p className="text-muted mb-4">
                    Our technical integration specialists will guide you through setup, custom margin slabs, and live key generation.
                  </p>

                  <div className="p-3 bg-white rounded-3 border mb-2 d-flex align-items-center">
                    <i className="fa-solid fa-phone text-warning fa-xl me-3"></i>
                    <div>
                      <div className="small text-muted font-weight-bold">Direct Hotline</div>
                      <a href="tel:+917311104571" className="text-dark font-weight-bold text-decoration-none">+91 7311104571</a>
                    </div>
                  </div>

                  <div className="p-3 bg-white rounded-3 border d-flex align-items-center">
                    <i className="fa-solid fa-envelope text-primary fa-xl me-3"></i>
                    <div>
                      <div className="small text-muted font-weight-bold">Sales &amp; Enterprise Support</div>
                      <a href="mailto:sales@mokshiq.in" className="text-dark font-weight-bold text-decoration-none">sales@mokshiq.in</a>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <form className="bg-white p-4 rounded-3 shadow-sm border" onSubmit={(e) => { e.preventDefault(); alert(`Thank you! Our ${titleParam} specialist will contact you shortly.`); }}>
                    <div className="mb-3">
                      <label className="form-label small text-muted font-weight-bold">Full Name / Company Name</label>
                      <input type="text" className="form-control" placeholder="e.g. Acme Fintech Pvt Ltd" required />
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label small text-muted font-weight-bold">Work Email</label>
                        <input type="email" className="form-control" placeholder="sales@mokshiq.in" required />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label small text-muted font-weight-bold">Mobile Number</label>
                        <input type="tel" className="form-control" placeholder="+91 7311104571" required />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted font-weight-bold">Selected Service</label>
                      <input type="text" className="form-control bg-light" value={titleParam} readOnly />
                    </div>
                    <button type="submit" className="btn btn-warning w-100 font-weight-bold py-2 text-dark">
                      Submit Request for {titleParam}
                    </button>
                  </form>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Dynamic FAQ Section */}
        <FAQ 
          faqList={dynamicFaqs}
          title={`${titleParam} FAQs`}
          subtitle={`Frequently asked technical and commercial questions regarding ${titleParam}.`}
        />

      </main>
      <Footer />
      <SeoTagsFooter />
    </div>
  );
}

export default ServiceDetail;